import { useEffect, useRef } from 'react';
import { MessagesDemande } from './MessagesDemande';
import { useMessagesDemande } from './useMessagesDemande';

const FOCALISABLES = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Les messages d'une demande PAR-DESSUS la page où l'on se trouve (la liste « Messages à envoyer ») : on ne la quitte pas.
 * ✕ et Échap ferment ; Tab reste dans la fenêtre ; le focus revient au bouton qui l'a ouverte.
 */
export function MessagesModal({ demandeId, titreSeance, onClose, onChange }: {
  demandeId: number; titreSeance: string; onClose: () => void; onChange: () => void;
}) {
  const { messages, chargement, erreur, recharger } = useMessagesDemande(demandeId);
  const fenetre = useRef<HTMLDivElement>(null);
  const fermer = useRef<HTMLButtonElement>(null);
  // `onClose` change à chaque rendu de la page : on le garde en référence pour que l'effet ne se rejoue pas (et ne vole pas le focus).
  const surFermeture = useRef(onClose);
  surFermeture.current = onClose;

  useEffect(() => {
    const origine = document.activeElement as HTMLElement | null;
    fermer.current?.focus();
    const surClavier = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); surFermeture.current(); return; }
      if (e.key !== 'Tab' || !fenetre.current) return;
      const elements = Array.from(fenetre.current.querySelectorAll<HTMLElement>(FOCALISABLES)).filter((n) => n.offsetParent !== null);
      if (elements.length === 0) return;
      const premier = elements[0], dernier = elements[elements.length - 1];
      const actif = document.activeElement as HTMLElement | null;
      if (!fenetre.current.contains(actif)) { e.preventDefault(); premier.focus(); }
      else if (e.shiftKey && actif === premier) { e.preventDefault(); dernier.focus(); }
      else if (!e.shiftKey && actif === dernier) { e.preventDefault(); premier.focus(); }
    };
    document.addEventListener('keydown', surClavier, true);
    return () => { document.removeEventListener('keydown', surClavier, true); origine?.focus?.(); };
  }, []);

  const apresAction = () => { void recharger(); onChange(); };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" role="dialog" aria-modal="true" aria-labelledby="messages-titre" ref={fenetre} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title" id="messages-titre">Messages · {titreSeance}</h3>
          <button ref={fermer} type="button" className="close-btn" aria-label="Fermer" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {chargement ? <div className="spinner" role="status" aria-label="Chargement des messages" />
            : erreur ? <div className="badge badge-err" style={{ display: 'block' }} role="alert">{erreur}</div>
            : <MessagesDemande demandeId={demandeId} messages={messages} titreSeance={titreSeance} onChange={apresAction} />}
        </div>
        <div className="modal-footer"><button type="button" className="btn" onClick={onClose}>Fermer</button></div>
      </div>
    </div>
  );
}
